import {
  ContentWorkflow,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { log } from '../../loggers/Logger';

const validPublicationStates = [
  WorkflowState.PUBLISHED,
  WorkflowState.FINALIZING,
  WorkflowState.FINALIZED,
  WorkflowState.DISTRIBUTED,
];

/** Determines whether the Reset Draft action can be performed on the given workflows. */
export function isResetDraftEnabled(workflows: ContentWorkflow[]): boolean {
  return workflows.every(
    (wf) =>
      // To reset the draft, we need at least 1 previously published version,
      // otherwise, there would be nothing to reset to:
      wf.publicationWorkflows?.length > 0 &&
      // The last published version must also be in certain valid states,
      // for example, it must not be unpublished:
      validPublicationStates.includes(wf.publicationState!) &&
      // Resetting draft only applicable to MODIFIED content, REJECTED content,
      // or UNMODIFIED but locked:
      (wf.draftState === WorkflowState.MODIFIED ||
        wf.draftState === WorkflowState.REJECTED ||
        (wf.draftState === WorkflowState.UNMODIFIED && wf.isLocked)),
  );
}

/** Posts RESET_DRAFT events for all workflows, shows flashbar feedback. */
export async function resetDraft(
  workflows: ContentWorkflow[],
  flashbarMessageBroker: FlashbarMessageBroker,
): Promise<boolean> {
  const didResetDraft = await Promise.all(
    workflows.map((wf) =>
      createWorkflowEventWithStatePolling(
        wf.contentId,
        wf.draftWorkflowId,
        WorkflowEventTypes.RESET_DRAFT,
        '',
      ),
    ),
  )
    .then(() => true)
    .catch((e) => {
      log.warn(e, 'Failed resetting draft');
      flashbarMessageBroker.pushErrorMessage('Failed to reset draft');
      return false;
    });

  if (didResetDraft) {
    flashbarMessageBroker.pushSuccessMessage('Draft has been reset successfully');
  }

  return didResetDraft;
}
